import { computed, inject, Injectable, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { UserClient } from '@psg/domains/user/data-access/user.data-access';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  readonly #userClient = inject(UserClient);

  // Resource that loads users
  readonly #userResource = rxResource({
    stream: () => this.#userClient.getAll(),
    defaultValue: [],
  });

  // Computed signal that creates a Map for quick lookups
  readonly usersMap: Signal<Map<string, { name: string; isAdmin: boolean }>> = computed(() => {
    const users = this.#userResource.hasValue() ? this.#userResource.value() : [];
    const map = new Map<string, { name: string; isAdmin: boolean }>();
    users.forEach((user) => map.set(user.id, { name: user.firstName + ' ' + user.lastName, isAdmin: user.isAdmin }));
    return map;
  });

  // Helper to get a user name by ID
  getUserName(id: string): Signal<string> {
    return computed(() => this.usersMap().get(id)?.name ?? 'Unknown User');
  }

  // Synchronous getter if you prefer
  getUserNameSync(id: string): string {
    return this.usersMap().get(id)?.name ?? 'Unknown User';
  }

  isAdminSync(userId: string): boolean {
    return this.usersMap().get(userId)?.isAdmin ?? false;
  }

  // Check loading state
  isLoading(): Signal<boolean> {
    return computed(() => this.#userResource.isLoading());
  }
}
